import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * One entry of a row's action menu.
 *
 * `disabled` carries `disabledReason` on purpose: an action a person can see but
 * not use has to say why, or the menu reads as broken.
 */
export interface RowAction {
  id: string;
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  disabledReason?: string;
}

export interface GenericRowActionsMenuProps {
  actions: RowAction[];
  /** Names the row the menu belongs to, so screen readers don't hear "more options" N times. */
  ariaLabel: string;
}

/** Ancho del panel (w-56) y alto aproximado de una entrada, para decidir si abre hacia arriba. */
const MENU_WIDTH = 224;
const ENTRY_HEIGHT = 36;
const PANEL_PADDING = 8;
const GAP = 4;
const VIEWPORT_MARGIN = 8;

interface MenuPosition {
  top: number;
  right: number;
}

/**
 * The overflow menu that carries a table row's actions.
 *
 * A dedicated column per action does not scale -- the second action forces the
 * table to be reshaped -- so the actions live behind one narrow column and the
 * next one costs an entry rather than a redesign.
 *
 * Renders nothing when there is no action to offer: an empty menu is a control
 * that promises something and delivers nothing.
 *
 * El panel se monta en un portal sobre `document.body` y se posiciona con
 * coordenadas `fixed`: dentro de la fila lo recortaba el contenedor con scroll
 * de la tabla, que en la última fila dejaba ver una franja de ocho píxeles. Por
 * lo mismo se cierra al scrollear -- una vez desprendido del flujo, seguir a la
 * fila costaría más de lo que vale.
 */
const GenericRowActionsMenu: React.FC<GenericRowActionsMenuProps> = ({
  actions,
  ariaLabel,
}) => {
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  const isOpen = position !== null;
  const close = useCallback(() => setPosition(null), []);

  const openMenu = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const height = actions.length * ENTRY_HEIGHT + PANEL_PADDING;
    const opensUpward = rect.bottom + GAP + height > window.innerHeight;

    setPosition({
      top: opensUpward ? rect.top - GAP - height : rect.bottom + GAP,
      right: Math.max(VIEWPORT_MARGIN, window.innerWidth - rect.right),
    });
  }, [actions.length]);

  // Cerrar al hacer click afuera, con Escape, o cuando la página se mueve bajo el
  // panel. Los listeners sólo viven mientras el menú está abierto: una tabla de
  // 50 filas monta 50 de estos.
  useEffect(() => {
    if (!isOpen) return undefined;

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      close();
      triggerRef.current?.focus();
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    // `capture` para enterarse también del scroll de la tabla, que no burbujea.
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [isOpen, close]);

  if (actions.length === 0) return null;

  const handleSelect = (action: RowAction) => {
    if (action.disabled) return;
    close();
    action.onSelect();
  };

  return (
    <div ref={containerRef} className="inline-block text-left">
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        onClick={() => (isOpen ? close() : openMenu())}
        className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md px-2 py-1 text-lg leading-none text-semantic-text-secondary sm:min-h-0 sm:min-w-0 hover:bg-semantic-bg-tertiary hover:text-semantic-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-semantic-border-brand-primary"
      >
        &#8942;
      </button>

      {position &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            style={{
              position: "fixed",
              top: position.top,
              right: position.right,
              width: MENU_WIDTH,
              zIndex: 9999,
            }}
            className="rounded-lg border border-semantic-border-primary bg-semantic-bg-primary p-1 shadow-lg"
          >
            {actions.map(action => (
              <button
                key={action.id}
                type="button"
                role="menuitem"
                disabled={action.disabled}
                title={action.disabled ? action.disabledReason : undefined}
                onClick={() => handleSelect(action)}
                className="block w-full rounded px-3 py-2 text-left text-sm text-semantic-text-primary hover:bg-semantic-bg-tertiary focus:outline-none focus-visible:bg-semantic-bg-tertiary disabled:cursor-not-allowed disabled:text-semantic-text-secondary disabled:hover:bg-transparent"
              >
                {action.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

export default GenericRowActionsMenu;
